import { PredictiveAddressInput } from "@/components/organisations/pav-address-input-v2";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CognitoUser } from "@aws-amplify/auth";
import { useQuery } from "@tanstack/react-query";
import { Auth } from "aws-amplify";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { Field, FieldError, FieldLabel, FieldSet } from "components/ui/field";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Skeleton } from "components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { workDescription } from "components/user/sign-in/states";
import { IdentityState, useIdentity, User } from "contexts/identity-context";
import * as QueryKeys from "data";
import { Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { resolveIdentityId } from "utils/identity";
import { object, string } from "yup";

import { StandardPageLayout } from "@/components/layouts/standard-page-layout";
import { Theme, useTheme } from "@/components/theme-provider";
import { Spinner } from "@/components/ui/spinner";
import { Info, Settings2 } from "lucide-react";
import { VerifyModal } from "./verify-modal";

type Mode = "Dark" | "Light" | "System";
const THEME_TO_MODE: Record<Theme, Mode> = {
  light: "Light",
  dark: "Dark",
  system: "System",
};
const MODE_TO_THEME: Record<Mode, Theme> = {
  Light: "light",
  Dark: "dark",
  System: "system",
};

export const UserProfile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingPreferences, setIsEditingPreferences] = useState(false);

  const [identity, setIdentity] = useIdentity();
  const identityId = resolveIdentityId(identity);
  const user = useRef<User>();
  const [cognitoUser, setCognitoUser] = useState<CognitoUser>(
    (identity as User).cognitoUser,
  );

  useEffect(() => {
    // Only copy once on first mount
    if (!user.current && identity && identity !== "loading") {
      user.current = { ...(identity as User) };
    }
  }, []);

  const authenticatedUserQuery = useQuery({
    queryKey: [QueryKeys.authenticatedUser, identityId],
    queryFn: () =>
      Auth.currentAuthenticatedUser({
        bypassCache: true,
      }),
    enabled: !isEditing,
    refetchOnWindowFocus: !isEditing,
    refetchOnMount: !isEditing,
    refetchOnReconnect: !isEditing,
  });

  useEffect(() => {
    if (authenticatedUserQuery.isSuccess && authenticatedUserQuery.data) {
      setCognitoUser(authenticatedUserQuery.data);
      const dynamicUser = authenticatedUserQuery.data as any;
      user.current = {
        ...user.current!,
        firstName: dynamicUser.attributes.given_name,
        lastName: dynamicUser.attributes.family_name,
        email: dynamicUser.attributes.email,
        phoneNumber: dynamicUser.attributes.phone_number,
        role: dynamicUser.attributes["custom:role"],
        workPhone: dynamicUser.attributes["custom:work_phone"],
        phoneNumberVerified: dynamicUser.attributes.phone_number_verified,
        emailVerified: dynamicUser.attributes.email_verified,
        address: dynamicUser.attributes.address,
      };
    }
  }, [authenticatedUserQuery.isSuccess]);

  const { theme, setTheme } = useTheme();

  const [mode, setMode] = useState<Mode>(THEME_TO_MODE[theme]);

  // Keep the dropdown in sync when the theme is changed elsewhere
  // (nav-user menu, header toggle, or the 'd' keybind).
  useEffect(() => {
    setMode(THEME_TO_MODE[theme]);
  }, [theme]);

  const [submitError, setSubmitError] = useState("");
  const [phoneVerifyOpen, setPhoneVerifyOpen] = useState(false);
  const [emailVerifyOpen, setEmailVerifyOpen] = useState(false);

  const [isPhoneUpdateLoading, setIsPhoneUpdateLoading] = useState(false);
  const [isEmailUpdateLoading, setIsEmailUpdateLoading] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);

  const handlePhoneUpdate = async (phoneNumber: string) => {
    setIsPhoneUpdateLoading(true);
    await Auth.updateUserAttributes(cognitoUser, {
      phone_number: phoneNumber,
    }).finally(() => {
      setIsPhoneUpdateLoading(false);
      setPhoneVerifyOpen(true);
    });
  };

  const handleEmailUpdate = async (email: string) => {
    setIsEmailUpdateLoading(true);
    await Auth.updateUserAttributes(cognitoUser, {
      email: email,
    }).finally(() => {
      setIsEmailUpdateLoading(false);
      setEmailVerifyOpen(true);
    });
  };

  const handleEmailVerificationSuccess = (email: string) => {
    setIdentity((prevIdentity: IdentityState) => {
      if (prevIdentity && prevIdentity !== "loading") {
        return {
          ...prevIdentity,
          email: email,
          emailVerified: true,
        };
      }
      return prevIdentity;
    });
    user.current = {
      ...user.current!,
      email: email,
      emailVerified: true,
    };
  };

  const handlePhoneVerificationSuccess = (phone: string) => {
    setIdentity((prevIdentity: IdentityState) => {
      if (prevIdentity && prevIdentity !== "loading") {
        return {
          ...prevIdentity,
          phoneNumber: phone,
          emailVerified: true,
        };
      }
      return prevIdentity;
    });
    user.current = {
      ...user.current!,
      phoneNumber: phone,
      phoneNumberVerified: true,
    };
  };

  const validationSchema = object().shape({
    firstName: string()
      .matches(
        /^[a-zA-Z\s'-]+$/,
        "First name can only contain letters, spaces, hyphens, and apostrophes",
      )
      .max(256, "First name must be at most 256 characters")
      .required("First name is required"),
    lastName: string()
      .matches(
        /^[a-zA-Z\s'-]+$/,
        "Last name can only contain letters, spaces, hyphens, and apostrophes",
      )
      .max(256, "Last name must be at most 256 characters")
      .required("Last name is required"),
    phoneNumber: string()
      .when([], {
        is: () => mfaEnabled,
        then: (schema) =>
          schema.required("Phone number is required when MFA is enabled"),
        otherwise: (schema) => schema.notRequired(),
      })
      .matches(
        /^\+61\d{9}$/,
        "Phone number must be in the format +61XXXXXXXXX",
      ),
    workPhone: string().matches(
      /^\+61\d{9}$/,
      "Work phone number must be in the format +61XXXXXXXXX",
    ),
    email: string()
      .max(256, "Email must be at most 256 characters")
      .email("Invalid email address")
      .required("Email is required"),
    address: string().nullable(true),
  });

  const handleThemeChange = () => {
    setTheme(MODE_TO_THEME[mode]);
  };

  const resetTheme = () => {
    setMode(THEME_TO_MODE[theme]);
  };

  const submitPreferences = () => {
    handleThemeChange();
    setIsEditingPreferences(false);
  };

  const cancelPreferences = () => {
    resetTheme();
  };

  const preferredMfaQuery = useQuery({
    queryKey: [QueryKeys.preferredMfa, identityId],
    queryFn: () =>
      Auth.getPreferredMFA(cognitoUser, {
        bypassCache: true,
      }),
    enabled: !!cognitoUser,
  });

  useEffect(() => {
    if (preferredMfaQuery.isSuccess && preferredMfaQuery.data) {
      setMfaEnabled(preferredMfaQuery.data !== "NOMFA");
    }
  }, [preferredMfaQuery.isSuccess]);

  const updateUserProfile = async (hubUser: User) => {
    await Auth.updateUserAttributes(cognitoUser, {
      given_name: hubUser.firstName,
      family_name: hubUser.lastName,
      phone_number: hubUser.phoneNumber ?? "",
      email: hubUser.email,
      "custom:work_phone": hubUser.workPhone ?? "",
      "custom:role": hubUser.role ?? "",
      address: hubUser.address ?? "",
    })
      .then(() => {
        setSubmitError("");
        setIdentity((prevIdentity: IdentityState) => {
          if (prevIdentity && prevIdentity !== "loading") {
            return {
              ...prevIdentity,
              firstName: hubUser.firstName,
              lastName: hubUser.lastName,
              email: hubUser.email,
              phoneNumber: hubUser.phoneNumber,
              role: hubUser.role,
              workPhone: hubUser.workPhone,
              phoneNumberVerified: (cognitoUser as any).attributes
                .phone_number_verified,
              emailVerified: (cognitoUser as any).attributes.email_verified,
              address: hubUser.address,
            };
          }
          return prevIdentity;
        });
        user.current = hubUser;
      })
      .catch((e) => {
        setSubmitError(e.message || "Something went wrong");
        throw new Error(e.message || "Something went wrong");
      })
      .finally(() => {
        setIsEditing(false);
        authenticatedUserQuery.refetch();
      });
  };

  return (
    <StandardPageLayout
      pageTitle={{
        icon: Settings2,
        title: "User General Settings",
        description:
          "Manage your user profile and preferences, including your name, contact information, and theme settings.",
      }}
    >
      <div className="mx-auto max-w-5xl space-y-4">
        <Card className="container mx-auto">
          <CardHeader>
            <CardTitle>User Profile</CardTitle>
          </CardHeader>
          {authenticatedUserQuery.isEnabled &&
            authenticatedUserQuery.isPending && (
              <CardContent>
                <Skeleton className="h-88 w-full" />
              </CardContent>
            )}
          {authenticatedUserQuery.isSuccess && user.current && (
            <Formik<User>
              enableReinitialize={true}
              initialValues={{
                ...user.current,
              }}
              onSubmit={async (values, { resetForm }) => {
                await updateUserProfile(values).catch(resetForm);
              }}
              validationSchema={validationSchema}
            >
              {({
                values,
                handleChange,
                isSubmitting,
                setFieldValue,
                handleBlur,
                errors,
                touched,
                resetForm,
                isValid,
                dirty,
              }: any) => (
                <Form>
                  <CardContent>
                    <FieldSet>
                      {submitError && (
                        <Alert variant="destructive">
                          <AlertTitle>{submitError}</AlertTitle>
                        </Alert>
                      )}
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <Field>
                          <FieldLabel htmlFor="firstName">
                            First Name
                          </FieldLabel>
                          <Input
                            id="firstName"
                            name="firstName"
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.firstName}
                            maxLength={256}
                            disabled={!isEditing}
                            aria-invalid={
                              touched.firstName && Boolean(errors.firstName)
                            }
                          />
                          {touched.firstName && errors.firstName && (
                            <FieldError>{errors.firstName}</FieldError>
                          )}
                        </Field>
                        <Field>
                          <FieldLabel htmlFor="lastName">Last Name</FieldLabel>
                          <Input
                            id="lastName"
                            name="lastName"
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.lastName}
                            maxLength={256}
                            disabled={!isEditing}
                            aria-invalid={
                              touched.lastName && Boolean(errors.lastName)
                            }
                          />
                          {touched.lastName && errors.lastName && (
                            <FieldError>{errors.lastName}</FieldError>
                          )}
                        </Field>
                      </div>
                      <Field>
                        <FieldLabel>Role</FieldLabel>
                        <Select
                          value={values.role}
                          onValueChange={(value) =>
                            setFieldValue("role", value)
                          }
                          disabled={!isEditing}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {workDescription
                              .sort((a, b) => a.localeCompare(b))
                              .map((service, index) => (
                                <SelectItem key={index} value={service}>
                                  {service}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="email">Email</FieldLabel>
                        <div className="flex items-start gap-2">
                          <div className="flex-1">
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.email}
                              maxLength={256}
                              disabled={!isEditing}
                              aria-invalid={
                                touched.email && Boolean(errors.email)
                              }
                            />
                            {touched.email && errors.email && (
                              <FieldError>{errors.email}</FieldError>
                            )}
                          </div>
                          {isEditing && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  onClick={() =>
                                    handleEmailUpdate(values.email)
                                  }
                                  disabled={
                                    isEmailUpdateLoading ||
                                    errors.email ||
                                    (values.emailVerified &&
                                      values.email == user.current!.email)
                                  }
                                >
                                  {isEmailUpdateLoading ? (
                                    <Spinner />
                                  ) : (
                                    "Verify"
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                Please verify your email before saving
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                        {isEditing && (
                          <Alert variant="info">
                            <Info className="size-4" />
                            <AlertTitle>Email Change Notice</AlertTitle>
                            <AlertDescription>
                              Changing your email address will also update your
                              login credentials. You'll use your new email
                              address to sign in.
                            </AlertDescription>
                          </Alert>
                        )}
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="address">Address</FieldLabel>
                        <PredictiveAddressInput
                          disabled={!isEditing}
                          readOnly={!isEditing}
                          id="address"
                          name="address"
                          value={values.address}
                          error={
                            touched.address !== null &&
                            touched.address &&
                            Boolean(errors.address)
                          }
                          helperText={touched.address && errors.address}
                          onBlur={handleBlur}
                          onChange={(val) => setFieldValue("address", val)}
                          onAddressSelect={(address, addressId) => {
                            setFieldValue("address", address);
                            setFieldValue("addressId", addressId);
                          }}
                          onClear={() => {
                            setFieldValue("address", "");
                            setFieldValue("addressId", "");
                          }}
                        />
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="workPhone">Work Phone</FieldLabel>
                        <Input
                          id="workPhone"
                          name="workPhone"
                          placeholder="+61xxxxxxxxx"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.workPhone}
                          disabled={!isEditing}
                          aria-invalid={
                            touched.workPhone && Boolean(errors.workPhone)
                          }
                        />
                        {touched.workPhone && errors.workPhone && (
                          <FieldError>{errors.workPhone}</FieldError>
                        )}
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="phoneNumber">Mobile</FieldLabel>
                        <div className="flex items-start gap-2">
                          <div className="flex-1">
                            <Input
                              id="phoneNumber"
                              name="phoneNumber"
                              placeholder="+61xxxxxxxxx"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.phoneNumber}
                              disabled={!isEditing}
                              aria-invalid={
                                touched.phoneNumber &&
                                Boolean(errors.phoneNumber)
                              }
                            />
                            {touched.phoneNumber && errors.phoneNumber && (
                              <FieldError>{errors.phoneNumber}</FieldError>
                            )}
                          </div>
                          {isEditing && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  onClick={() =>
                                    handlePhoneUpdate(values.phoneNumber)
                                  }
                                  disabled={
                                    isPhoneUpdateLoading ||
                                    errors.phoneNumber ||
                                    !values.phoneNumber ||
                                    (values.phoneNumberVerified &&
                                      values.phoneNumber ==
                                        user.current!.phoneNumber)
                                  }
                                >
                                  {isPhoneUpdateLoading ? (
                                    <Spinner />
                                  ) : (
                                    "Verify"
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                Please verify your phone number before saving
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </Field>
                    </FieldSet>
                  </CardContent>
                  <CardFooter className="flex justify-end gap-2 mt-4">
                    {!isEditing && (
                      <Button onClick={() => setIsEditing(true)}>Edit</Button>
                    )}
                    {isEditing && (
                      <>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            resetForm();
                            setIsEditing(false);
                            setSubmitError("");
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          disabled={
                            isSubmitting ||
                            !isValid ||
                            !dirty ||
                            (values.phoneNumber &&
                              values.phoneNumber !==
                                user.current!.phoneNumber) ||
                            values.email !== user.current!.email
                          }
                        >
                          {isSubmitting ? (
                            <>
                              <Spinner />
                              Saving...
                            </>
                          ) : (
                            "Save"
                          )}
                        </Button>
                      </>
                    )}
                  </CardFooter>
                  <VerifyModal
                    isCodeSent={true}
                    open={emailVerifyOpen}
                    title="Verify your email"
                    subheader={`We've sent an email with a verification code to your email ${values.email}`}
                    attribute="email"
                    onOpenChange={(open) => {
                      if (!open) {
                        setEmailVerifyOpen(false);
                        authenticatedUserQuery.refetch();
                      }
                    }}
                    onSuccess={() =>
                      handleEmailVerificationSuccess(values.email)
                    }
                  />
                  <VerifyModal
                    isCodeSent={true}
                    open={phoneVerifyOpen}
                    title="Verify your phone number"
                    subheader={`We've sent an SMS with a verification code to your phone ${values.phoneNumber}`}
                    attribute="phone_number"
                    onOpenChange={(open) => {
                      if (!open) {
                        setPhoneVerifyOpen(false);
                        authenticatedUserQuery.refetch();
                      }
                    }}
                    onSuccess={() =>
                      handlePhoneVerificationSuccess(values.phoneNumber)
                    }
                  />
                </Form>
              )}
            </Formik>
          )}
        </Card>
        <Card className="container mx-auto">
          <CardHeader>
            <CardTitle>User Preferences</CardTitle>
          </CardHeader>
          <CardContent>
            <FieldSet>
              <Field>
                <FieldLabel>Theme</FieldLabel>
                <Select
                  value={mode}
                  onValueChange={(value) => setMode(value as Mode)}
                  disabled={!isEditingPreferences}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Light">Light</SelectItem>
                    <SelectItem value="Dark">Dark</SelectItem>
                    <SelectItem value="System">System</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field>
                <FieldLabel htmlFor="language">Language</FieldLabel>
                <Input
                  id="language"
                  name="language"
                  type="text"
                  value="English (UK)"
                  disabled
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="timezone">Time Zone</FieldLabel>
                <Input
                  id="timezone"
                  name="timezone"
                  type="text"
                  value="Australia/Sydney (+11:00)"
                  disabled
                />
              </Field>
            </FieldSet>
          </CardContent>
          <CardFooter className="flex justify-end gap-2">
            {!isEditingPreferences && (
              <Button onClick={() => setIsEditingPreferences(true)}>
                Edit
              </Button>
            )}
            {isEditingPreferences && (
              <>
                <Button
                  variant="ghost"
                  onClick={() => {
                    cancelPreferences();
                    setIsEditingPreferences(false);
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={submitPreferences}>Save</Button>
              </>
            )}
          </CardFooter>
        </Card>
      </div>
    </StandardPageLayout>
  );
};
